// vendors
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryCache } from 'react-query';

/* Components */
import { Button } from 'lego-dist';
import DefaultPermissions from '../sharedComponents/DefaultPermissions';
import SpecialPermissions from './components/SpecialPermissions';

/* Styled Components */
import {
  PermissionContainer,
  PermissionGroup,
  SaveBtn,
  StylesImageModalResultsUpdateMember,
} from './styles/PermissionContainer';

/* Hooks */
import useTeacher from 'hooks/useTeacher';
import { useRouter } from 'routes';
import { teacherQuery } from '../queryConfig';

import updateErrorImage from 'images/error.svg';
import updateSuccessImage from 'images/checked.svg';
import RendererIf from 'components/RendererIf';
import ResultActionModal from 'components/shared/ResultActionModal/ResultActionModal';

interface SettingsFormProps {
  onUpdateInformation?: (data: any) => any;
}

const SettingsForm: React.FC<SettingsFormProps> = ({
  onUpdateInformation,
}) => {
  const { t } = useTranslation();
  const queryCache = useQueryCache();
  const teacherData: any = queryCache.getQueryData(teacherQuery.getTeacher.key);
  const { user } = teacherData;
  const { updateTeacherPermissions } = useTeacher();

  /* get center id */
  const {
    currentParamsRef: {
      current: { centerId },
    },
  } = useRouter();

  const [showResultActionModal, setShowResultActionModal] = useState<any>(
    false,
  );

  const ConfigResultActionModal = {
    success: {
      title: t('common:SUCCESS'),
      image: updateSuccessImage,
      configPopsModal: {
        showCloseButton: false,
        onClosedModal: () => {
          if (setShowResultActionModal) setShowResultActionModal(false);
        },
      },
    },
    error: {
      title: t('common:ERROR'),
      image: updateErrorImage,
      configPopsModal: {
        showCloseButton: true,
        onClosedModal: () => {
          if (setShowResultActionModal) setShowResultActionModal(false);
        },
      },
    },
  };

  const [statusResultActionModal, setStatusResultActionModal] = useState<any>(
    ConfigResultActionModal.error,
  );

  /* Permissions Logic */
  const [editedPermissions, setEditedPermissions] = useState<any>();

  // track if changes have occured to enable Save button
  const [changeOccured, setChangeOccured] = useState<boolean>(true);

  const handleSave = () => {
    const reqBody = {
      user: {
        permission_ids: editedPermissions,
      },
    };

    updateTeacherPermissions(centerId, user.id, reqBody)
      .then(() => {
        if (onUpdateInformation) {
          onUpdateInformation({ tab: 'settings' });
        }
        setStatusResultActionModal(ConfigResultActionModal.success);
      }).catch(() => {
        setStatusResultActionModal(ConfigResultActionModal.error);
      }).finally(() => setShowResultActionModal(new Date()));
  };

  return (
    <PermissionContainer>
      <RendererIf condition={showResultActionModal}>
        <ResultActionModal
          styles={{ Image: StylesImageModalResultsUpdateMember }}
          {...statusResultActionModal}
        />
      </RendererIf>
      <PermissionGroup>
        <h3>
          {t(
            'settings:STAFF_SECTION.MANAGE.ADD_MEMBER_VIEW.STEPS.PERMISSIONS.DEFAULT.TITLE',
          )}
        </h3>
        <DefaultPermissions role={user.type} />
      </PermissionGroup>
      <PermissionGroup>
        <h3>
          {t(
            'settings:STAFF_SECTION.MANAGE.ADD_MEMBER_VIEW.STEPS.PERMISSIONS.SPECIAL.TITLE',
          )}
        </h3>
        <SpecialPermissions
          teacherId={user.id.toString()}
          teacherPermissions={user.permissions}
          enableButton={setChangeOccured}
          editedPermissions={editedPermissions}
          handlePermissionChange={setEditedPermissions}
        />
      </PermissionGroup>
      <SaveBtn>
        <Button
          rounded
          flat
          color="#75B753"
          disabled={changeOccured}
          onClick={() => handleSave()}
        >
          {t('SAVE')}
        </Button>
      </SaveBtn>
    </PermissionContainer>
  );
};

export default SettingsForm;
